import { Injectable, signal } from '@angular/core';
import { catchError, from, map, Observable, of, shareReplay, switchMap, tap, finalize } from 'rxjs';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';
import { CurrentEmployeeService } from './auth/current-employee.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    public readonly user = this.currentEmployeeService.user;
    public readonly isAuthenticated = signal<boolean | null>(null);
    private initAuth$?: Observable<boolean>;

    constructor(
        private msalService: MsalService,
        private currentEmployeeService: CurrentEmployeeService,
        private router: Router
    ) {}

    public initAuth(): Observable<boolean> {
        if (this.initAuth$) {
            return this.initAuth$;
        }

        this.initAuth$ = from(this.msalService.instance.initialize()).pipe(
            switchMap(() => this.msalService.handleRedirectObservable()),
            switchMap((authResult) => this.handleAuthenticationResult(authResult)),
            catchError((error) => {
                console.error('Authentication initialization failed', error);
                return this.redirectToNoAccess();
            }),
            finalize(() => {
                this.initAuth$ = undefined;
            }),
            shareReplay(1)
        );

        return this.initAuth$;
    }

    public login(): void {
        void this.msalService.loginRedirect(environment.loginRequest);
    }

    public logout(): void {
        this.clearAuthenticationState();
        void this.msalService.logoutRedirect({
            postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri
        });
    }

    private handleAuthenticationResult(authResult: AuthenticationResult | null): Observable<boolean> {
        const activeAccount =
            authResult?.account ??
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0] ??
            null;

        if (!activeAccount) {
            this.clearAuthenticationState();
            return of(false);
        }

        this.msalService.instance.setActiveAccount(activeAccount);
        return this.currentEmployeeService.load(activeAccount).pipe(
            tap((isAuthenticated) => {
                this.isAuthenticated.set(isAuthenticated);
            })
        );
    }

    private clearAuthenticationState(): void {
        this.currentEmployeeService.clear();
        this.isAuthenticated.set(false);
    }

    private redirectToNoAccess(): Observable<boolean> {
        this.clearAuthenticationState();
        return from(this.router.navigateByUrl('/no-access')).pipe(map(() => false));
    }
}
